import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from './api';
import {
  ATHENA_REMOTE_CONFIG_DEFAULTS,
  isAthenaRemoteFeatureEnabled,
  resolveAthenaRemoteConfig,
} from './remoteConfig';
import { getAthenaVersionInfo } from './version';
import { getAthenaNativeInstallation, getAthenaRuntimeCapabilities } from '../platform/AthenaDeviceContext';
import { useAuth } from '../context/AuthContext';

const RemoteConfigContext = createContext(null);
const EMPTY_RESOLUTION = Object.freeze(resolveAthenaRemoteConfig(null));

const SERVER_CAPABILITIES = new Set(['context_events_v1', 'quick_capture_v1', 'remote_config_v1']);

function safeServerCapabilityList(values) {
  const requested = Array.isArray(values) ? values : [];
  return Object.freeze([...new Set(requested.filter((value) => (
    typeof value === 'string' && SERVER_CAPABILITIES.has(value)
  )))].sort());
}

function bootstrapPayload(response) {
  const root = response && typeof response === 'object' ? response : {};
  return root.bootstrap && typeof root.bootstrap === 'object' ? root.bootstrap : root;
}

async function authenticatedPost(path, token, body) {
  if (!token) return null;
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

/**
 * Authenticated, bounded feature negotiation. It does not download a bundle,
 * execute server configuration, request a permission, or activate a feature.
 */
export function AthenaRemoteConfigProvider({ children }) {
  const { user } = useAuth();
  const [resolution, setResolution] = useState(EMPTY_RESOLUTION);
  const [status, setStatus] = useState('defaults');
  const [negotiatedCapabilities, setNegotiatedCapabilities] = useState(Object.freeze([]));
  const [runtime, setRuntime] = useState(null);
  const activeRequestRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++activeRequestRef.current;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('athena_token') : null;
    if (!user || !token) {
      setResolution(EMPTY_RESOLUTION);
      setNegotiatedCapabilities(Object.freeze([]));
      setRuntime(null);
      setStatus('defaults');
      return { ok: false, reason: 'authentication_required' };
    }

    setStatus('loading');
    const [version, runtimeCapabilities, installation] = await Promise.all([
      Promise.resolve(getAthenaVersionInfo()),
      getAthenaRuntimeCapabilities(),
      getAthenaNativeInstallation(),
    ]);
    if (requestId !== activeRequestRef.current) return { ok: false, reason: 'superseded' };
    setRuntime({ version, runtimeCapabilities });

    // PWA retains the conservative local defaults. The Android-only Worker
    // contract requires an installation proof produced by the native bridge.
    if (!installation || runtimeCapabilities.platform !== 'android' || !Number.isInteger(runtimeCapabilities.nativeVersionCode)) {
      setResolution(EMPTY_RESOLUTION);
      setNegotiatedCapabilities(Object.freeze([]));
      setStatus('defaults');
      return { ok: true, source: installation ? 'native_registration_incomplete' : 'pwa_defaults' };
    }

    const request = {
      installation_id: installation.installationId,
      installation_proof: installation.installationProof,
      platform: 'android',
      native_version_code: runtimeCapabilities.nativeVersionCode,
      app_version: version.appVersion,
      web_bundle_version: version.webBundleVersion,
      api_version: version.apiVersion,
      capability_version: runtimeCapabilities.capabilityVersion,
      capabilities: runtimeCapabilities.capabilities,
    };

    // Registration is idempotent on the Worker. It may be absent during a
    // rolling release; bootstrap below still remains a safe default-off probe.
    await authenticatedPost('/devices/register', token, request);
    // Bootstrap has a narrower contract than registration. Sending version or
    // capability fields here is rejected by the Worker rather than ignored.
    const response = await authenticatedPost('/client/bootstrap', token, {
      installation_id: installation.installationId,
      installation_proof: installation.installationProof,
    });
    if (requestId !== activeRequestRef.current) return { ok: false, reason: 'superseded' };
    if (!response) {
      setResolution(EMPTY_RESOLUTION);
      setNegotiatedCapabilities(Object.freeze([]));
      setStatus('unavailable');
      return { ok: false, reason: 'bootstrap_unavailable' };
    }

    const bootstrap = bootstrapPayload(response);
    const configPayload = bootstrap?.remote_config && typeof bootstrap.remote_config === 'object'
      ? bootstrap.remote_config
      : bootstrap;
    const nextResolution = resolveAthenaRemoteConfig(configPayload);
    const remoteCapabilities = bootstrap?.server_capabilities || bootstrap?.capabilities || response?.server_capabilities || response?.capabilities || [];
    setResolution(Object.freeze(nextResolution));
    // Server capability labels describe the API contract, not a native
    // feature. Keep them separate from the locally advertised capabilities so
    // diagnostics do not falsely claim a missing Android surface is enabled.
    setNegotiatedCapabilities(safeServerCapabilityList(remoteCapabilities));
    setStatus('ready');
    return { ok: true, resolution: nextResolution };
  }, [user]);

  useEffect(() => {
    void refresh();
    return () => { activeRequestRef.current += 1; };
  }, [refresh]);

  const featureEnabled = useCallback((feature) => (
    isAthenaRemoteFeatureEnabled(resolution.config || ATHENA_REMOTE_CONFIG_DEFAULTS, feature)
  ), [resolution]);

  const value = useMemo(() => ({
    config: resolution.config,
    source: resolution.source,
    schemaValid: resolution.schemaValid,
    status,
    negotiatedCapabilities,
    runtime,
    featureEnabled,
    refresh,
  }), [featureEnabled, negotiatedCapabilities, refresh, resolution.config, resolution.schemaValid, resolution.source, runtime, status]);

  return <RemoteConfigContext.Provider value={value}>{children}</RemoteConfigContext.Provider>;
}

export function useAthenaRemoteConfig() {
  const context = useContext(RemoteConfigContext);
  if (!context) throw new Error('useAthenaRemoteConfig must be used inside AthenaRemoteConfigProvider');
  return context;
}
